import { Button, Result, Space, Typography } from "antd";
import {
  ClockCircleOutlined,
  LogoutOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";

const { Paragraph, Text } = Typography;

/**
 * What an unapproved account sees instead of the app.
 *
 * This is a courtesy screen, not the security boundary — every API route
 * independently rejects an unapproved user with 403 PENDING_APPROVAL, so
 * bypassing this screen gets you nothing.
 */
export default function PendingApproval({ status, email, onRetry, onLogout }) {
  const revoked = ["denied", "banned"].includes(
    String(status || "").toLowerCase(),
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "var(--app-bg)",
      }}
    >
      <div
        className="app-panel"
        style={{ maxWidth: 520, width: "100%", padding: 8 }}
      >
        <Result
          icon={
            revoked ? (
              <StopOutlined style={{ color: "#ff4d4f" }} />
            ) : (
              <ClockCircleOutlined style={{ color: "var(--brand-500)" }} />
            )
          }
          title={revoked ? "Access revoked" : "Waiting for approval"}
          subTitle={
            revoked
              ? "An admin has removed your access to this workspace."
              : "Your account was created. An admin needs to approve it before you can use the app."
          }
          extra={
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {!revoked ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  You'll get in as soon as someone on the team approves or
                  invites <Text code>{email}</Text> — including if the invite
                  arrives after you signed up. Once that happens, hit{" "}
                  <Text strong>Check again</Text> and you're in. If you were
                  expecting an invite, make sure you signed up with the exact
                  address it was sent to.
                </Paragraph>
              ) : (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  If you think this is a mistake, contact an admin.
                </Paragraph>
              )}

              <Space>
                {!revoked ? (
                  <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
                    Check again
                  </Button>
                ) : null}
                <Button icon={<LogoutOutlined />} onClick={onLogout}>
                  Sign out
                </Button>
              </Space>
            </Space>
          }
        />
      </div>
    </div>
  );
}
